import { useGenerateMessageRecommendationMutation } from '@copilot/data/graphql/_generated';
import notificationManager from '@copilot/common/utils/notificationManager';
import { ReplyRecommendationHookData } from './types';

/**
 * Props for the useReplyRecommendation hook
 */
export type UseReplyRecommendationProps = {
    /** The org member ID */
    orgMemberId: string;
    /** The source profile ID (user) */
    sourceProfileId: string;
    /** The target profile ID (lead) */
    targetProfileId: string;
};

/**
 * Hook to handle reply recommendation generation
 */
export function useReplyRecommendation({
    orgMemberId,
    sourceProfileId,
    targetProfileId,
}: UseReplyRecommendationProps): ReplyRecommendationHookData {
    const [generateMessageRecommendation, { loading: isRegenerating }] =
        useGenerateMessageRecommendationMutation();

    /**
     * Regenerate the ai recommended message
     * @returns The new message or undefined if the regeneration fails
     */
    async function regenerateMessage(): Promise<string | undefined> {
        try {
            const result = await generateMessageRecommendation({
                variables: {
                    input: {
                        orgMemberId,
                        sourceProfileId,
                        targetProfileId,
                    },
                },
            });

            if (result.data?.generateMessageRecommendation?.content) {
                notificationManager.showSuccessNotification({
                    message: 'Message regenerated successfully',
                    description: 'A new recommended message has been generated.',
                });
                return result.data.generateMessageRecommendation.content;
            }
        } catch (error) {
            console.error('Error regenerating message:', error);
            notificationManager.showErrorNotification({
                message: 'Failed to regenerate message',
                description: 'Please try again later.',
            });
        }
        return undefined;
    }

    return {
        regenerateMessage,
        isRegenerating,
    };
}
